import { Table } from "common/Table/Table"
import { ReceiptPaidStatusControl } from "components/ReceiptPaidStatusControl"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { userSelector } from "appRedux/selectors/user"
import UserService from "services/user.service"
import { hasPermission } from "utils/permissions"
import {
  exportToCSV,
  formatDate,
  formatExportDate,
  type ExportColumnDef,
  parseExportNumber,
} from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import { EvoFieldLabel, EvoSelect, EvoStatTile } from "components/evo"

const service = new UserService()

const receiptVendorLabel = (row: {
  vendorName?: string | null
  expenseDescription?: string | null
}) => row.vendorName?.trim() || row.expenseDescription?.trim() || ""

const RECEIPT_TRANSACTION_EXPORT_COLUMNS: ExportColumnDef[] = [
  { dbColName: "badgeId", title: "Badge ID" },
  { dbColName: "fullName", title: "Full Name" },
  { dbColName: "maxPosition", title: "Max Position" },
  { dbColName: "dateCreated", title: "Date Created" },
  { dbColName: "categoryLabel", title: "Category" },
  { dbColName: "vendorName", title: "Vendor" },
  { dbColName: "expenseType", title: "Expense Type" },
  { dbColName: "city", title: "City" },
  { dbColName: "submissionDate", title: "Submission Date" },
  { dbColName: "amount", title: "Amount", decimals: 2 },
  { dbColName: "receiptPaidStatus", title: "Paid status" },
]

const formatCurrency = (n: number) =>
  typeof n === "number" && !isNaN(n) ? `$${n.toFixed(2)}` : "—"

export const ReceiptTransactions = () => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [totalItems, setTotalItems] = useState(0)
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 50,
  })
  const [category, setCategory] = useState<"all" | "trips" | "operations">(
    "all"
  )
  const [updatingKey, setUpdatingKey] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const currentUser = useSelector(userSelector)
  const canEditPaid = hasPermission(
    currentUser,
    "receiptTransactions",
    "edit"
  )

  const { successMessage, errorMessage } = useNotifications()

  const fetchData = useCallback(() => {
    setLoading(true)
    service
      .getReceiptTransactions(pagination, category, {
        sortOrder: "asc",
        submittedOnly: false,
      })
      .then((resp) => {
        setData(resp.data.data)
        setTotalItems(resp.data.totalDocument)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [pagination, category])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    setPagination((p) => ({ ...p, pageNumber: 1 }))
  }, [category])

  const columns = [
    {
      key: "badgeId",
      title: "Badge ID",
      freeze: true,
      freezeIndex: "left-0",
      render: (row: any) => row.badgeId ?? "—",
    },
    {
      key: "fullName",
      title: "Full Name",
      render: (row: any) => row.fullName ?? "—",
    },
    {
      key: "maxPosition",
      title: "Max Position",
      render: (row: any) => row.maxPosition ?? "—",
    },
    {
      key: "dateCreated",
      title: "Date Created",
      render: (row: any) =>
        row.dateCreated ? formatDate(row.dateCreated) : "—",
    },
    {
      key: "categoryLabel",
      title: "Category",
      render: (row: any) => row.categoryLabel ?? "—",
    },
    {
      key: "submissionDate",
      title: "Submission Date",
      render: (row: any) =>
        row.submissionDate ? formatDate(row.submissionDate) : "—",
    },
    {
      key: "amount",
      title: "Amount",
      render: (row: any) => formatCurrency(row.amount),
    },
    {
      key: "receiptPaidStatus",
      title: "Paid status",
      freeze: true,
      freezeIndex: "right-0",
      render: (row: any) => {
        const expenseId =
          row.expenseId?.toString?.() ?? String(row.expenseId)
        const parentId = row.parentId?.toString?.() ?? String(row.parentId)
        const uk = `${row.source}:${parentId}:${expenseId}`
        const paid =
          row.receiptPaidStatus === "Paid" ||
          row.receiptPaidStatus === "Not-Paid"
            ? row.receiptPaidStatus
            : "Not-Paid"
        return (
          <ReceiptPaidStatusControl
            value={paid}
            saving={updatingKey === uk}
            disabled={!canEditPaid}
            onChange={
              canEditPaid
                ? async (next) => {
                    setUpdatingKey(uk)
                    try {
                      await service.updateReceiptPaidStatus({
                        source: row.source,
                        parentId,
                        expenseId,
                        receiptPaidStatus: next,
                      })
                      successMessage("Payment status updated")
                      fetchData()
                    } catch (e: any) {
                      errorMessage(
                        e?.response?.data?.message ||
                          "Could not update payment status"
                      )
                    } finally {
                      setUpdatingKey(null)
                    }
                  }
                : undefined
            }
          />
        )
      },
    },
  ]

  const handleExport = async () => {
    setExporting(true)
    try {
      const resp = await service.getReceiptTransactions(
        { ...pagination, isDownload: "true" },
        category,
        {
          sortOrder: "asc",
          submittedOnly: false,
        }
      )
      const list = resp.data.data ?? []
      const rows = list.map((row: any) => ({
        badgeId: String(row.badgeId ?? ""),
        fullName: String(row.fullName ?? ""),
        maxPosition: String(row.maxPosition ?? ""),
        dateCreated: formatExportDate(row.dateCreated),
        categoryLabel: String(row.categoryLabel ?? ""),
        submissionDate: formatExportDate(row.submissionDate),
        amount: parseExportNumber(row.amount) ?? 0,
        receiptPaidStatus: String(row.receiptPaidStatus ?? ""),
      }))
      exportToCSV(rows, `receipt-transactions-${category}`, {
        columns: RECEIPT_TRANSACTION_EXPORT_COLUMNS,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="grid max-w-xs grid-cols-1 gap-3 sm:grid-cols-2">
          <EvoStatTile
            label="Receipts"
            value={loading ? "—" : totalItems}
            hint={
              category === "all"
                ? "Trips and operations"
                : category === "trips"
                  ? "Trips only"
                  : "Operations only"
            }
          />
        </div>
        <div className="flex min-w-[9rem] flex-col gap-1">
          <EvoFieldLabel>Category</EvoFieldLabel>
          <EvoSelect
            value={category}
            onChange={(e) =>
              setCategory(e.target.value as "all" | "trips" | "operations")
            }
            className="min-w-[9rem]"
          >
            <option value="all">All</option>
            <option value="trips">Trips</option>
            <option value="operations">Operations</option>
          </EvoSelect>
        </div>
      </div>
      <Table
        data={data}
        loadingData={loading}
        setData={setData}
        totalItems={totalItems}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        exportButton={true}
        handleExport={handleExport}
        exporting={exporting}
      />
    </div>
  )
}
